import { useEffect, useRef } from 'react'

/**
 * The only colour on the marketing site.
 *
 * Deliberately canvas 2D rather than WebGL: layered sine curves with a gradient stroke get
 * the silk-ribbon read at a few kilobytes, where a 3D library would cost hundreds. The whole
 * palette is warm sand against periwinkle so it sits on the paper ground instead of on top of it.
 */

interface Band {
  /** Vertical centre as a fraction of height. */
  y: number
  amplitude: number
  /** Wavelengths across the full width. */
  frequency: number
  speed: number
  phase: number
  thickness: number
  from: [number, number, number]
  to: [number, number, number]
  alpha: number
}

const BANDS: Band[] = [
  {
    y: 0.62, amplitude: 0.1, frequency: 1.1, speed: 0.05, phase: 0,
    thickness: 0.2, from: [214, 201, 168], to: [143, 162, 216], alpha: 0.5,
  },
  {
    y: 0.66, amplitude: 0.13, frequency: 0.85, speed: -0.035, phase: 2.1,
    thickness: 0.15, from: [201, 180, 138], to: [107, 127, 196], alpha: 0.42,
  },
  {
    y: 0.58, amplitude: 0.08, frequency: 1.45, speed: 0.07, phase: 4.3,
    thickness: 0.1, from: [230, 219, 194], to: [166, 180, 224], alpha: 0.36,
  },
  {
    y: 0.71, amplitude: 0.06, frequency: 1.9, speed: -0.06, phase: 1.2,
    thickness: 0.06, from: [188, 165, 122], to: [126, 143, 205], alpha: 0.3,
  },
]

const rgba = (c: [number, number, number], a: number) => `rgba(${c[0]},${c[1]},${c[2]},${a})`

export function HeroRibbon() {
  const ref = useRef<HTMLCanvasElement>(null)

  useEffect(() => {
    const canvas = ref.current
    if (!canvas) return
    const ctx = canvas.getContext('2d')
    if (!ctx) return

    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches
    let width = 0
    let height = 0
    let frame = 0
    let visible = true
    // Advanced by elapsed time rather than frame count, so the drift runs at the same
    // speed on a 120Hz display as on a 60Hz one.
    let clock = 0
    let last = 0

    const resize = () => {
      const rect = canvas.getBoundingClientRect()
      // Capped at 2x: past that the extra pixels cost real memory and nobody can see them.
      const dpr = Math.min(window.devicePixelRatio || 1, 2)
      width = rect.width
      height = rect.height
      canvas.width = Math.round(width * dpr)
      canvas.height = Math.round(height * dpr)
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0)
    }

    const draw = () => {
      ctx.clearRect(0, 0, width, height)
      ctx.lineCap = 'round'

      for (const band of BANDS) {
        const gradient = ctx.createLinearGradient(0, 0, width, 0)
        gradient.addColorStop(0, rgba(band.from, 0))
        gradient.addColorStop(0.25, rgba(band.from, band.alpha))
        gradient.addColorStop(0.6, rgba(band.to, band.alpha))
        gradient.addColorStop(1, rgba(band.to, 0))

        ctx.strokeStyle = gradient
        ctx.lineWidth = Math.max(2, band.thickness * height)
        ctx.beginPath()

        const step = Math.max(4, width / 180)
        for (let x = 0; x <= width; x += step) {
          const t = x / Math.max(width, 1)
          const angle = t * band.frequency * Math.PI * 2 + band.phase + clock * band.speed
          // A second, slower harmonic keeps the curve from looking like a plain sine.
          const y =
            band.y * height +
            Math.sin(angle) * band.amplitude * height +
            Math.sin(angle * 0.5 + band.phase) * band.amplitude * height * 0.3
          if (x === 0) ctx.moveTo(x, y)
          else ctx.lineTo(x, y)
        }
        ctx.stroke()
      }
    }

    const tick = (now: number) => {
      if (visible) {
        clock += last ? (now - last) / 1000 : 0
        draw()
      }
      last = now
      frame = requestAnimationFrame(tick)
    }

    resize()
    if (reduceMotion) {
      // Still render — the graphic carries the page's only colour — but hold it still.
      draw()
    } else {
      frame = requestAnimationFrame(tick)
    }

    const onResize = () => {
      resize()
      draw()
    }
    window.addEventListener('resize', onResize)

    // Stop burning frames once the hero scrolls away.
    const observer = new IntersectionObserver(
      ([entry]) => {
        visible = entry.isIntersecting
        // Reset the delta so the ribbon does not jump forward on re-entry.
        if (visible) last = 0
      },
      { threshold: 0 },
    )
    observer.observe(canvas)

    return () => {
      cancelAnimationFrame(frame)
      window.removeEventListener('resize', onResize)
      observer.disconnect()
    }
  }, [])

  return <canvas ref={ref} className="hero-canvas" aria-hidden="true" />
}
